import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { fetchResumeData } from '../../fetchProjects';
import { sanitizeHtml } from '../../utils/sanitize';

const AwardDetail = () => {
  const { idSlug } = useParams();
  const navigate   = useNavigate();
  const id = idSlug ? idSlug.split('-')[0] : null;

  const [item, setItem]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx]   = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchResumeData().then(data => {
      const found = data.find(i => String(i.id) === String(id));
      setItem(found || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const parseImages = (raw) => {
    try { const a = JSON.parse(raw || '[]'); return Array.isArray(a) ? a.filter(Boolean) : []; }
    catch { return raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []; }
  };

  const openLightbox = (i) => { setLightboxIdx(i); setLightboxOpen(true); };

  const handleKey = useCallback((e) => {
    if (!lightboxOpen) return;
    if (e.key === 'ArrowRight') setLightboxIdx(i => Math.min(i + 1, images.length - 1));
    if (e.key === 'ArrowLeft')  setLightboxIdx(i => Math.max(i - 1, 0));
    if (e.key === 'Escape')     setLightboxOpen(false);
  }, [lightboxOpen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-text-primary">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-accent" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-background text-text-primary">
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>Award Not Found | Sudhan R</title>
        </Helmet>
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Icon name="AlertCircle" size={48} className="text-red-400" />
          <h2 className="text-2xl font-bold">Award Not Found</h2>
          <button onClick={() => navigate(-1)} className="text-accent hover:underline">Go Back</button>
        </div>
        <Footer />
      </div>
    );
  }

  const images = parseImages(item.images);
  const bulletPoints = Array.isArray(item.achievements) ? item.achievements.filter(Boolean)
    : (item.achievements ? (() => { try { return JSON.parse(item.achievements); } catch { return []; } })() : []);
  const technologies = Array.isArray(item.technologies) ? item.technologies.filter(Boolean)
    : (item.technologies ? (() => { try { return JSON.parse(item.technologies); } catch { return []; } })() : []);

  return (
    <div className="min-h-screen bg-background text-text-primary selection:bg-accent/30 selection:text-white">
      <Helmet>
        <title>{item.title} | Sudhan R Awards & Honors</title>
        <meta name="description" content={item.description || item.title} />
        <meta property="og:title" content={item.title} />
        {images[0] && <meta property="og:image" content={images[0]} />}
        <link rel="canonical" href={`https://sudhan.live/award/${idSlug}`} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "CreativeWork",
                "@id": `https://sudhan.live/award/${idSlug}#award`,
                "name": item.title,
                "description": item.description || "",
                "author": {
                  "@id": "https://sudhan.live/#person"
                },
                "publisher": {
                  "@id": "https://sudhan.live/#person"
                },
                "isPartOf": {
                  "@id": "https://sudhan.live/#website"
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://sudhan.live/award/${idSlug}`
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": `https://sudhan.live/award/${idSlug}#breadcrumb`,
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Resume",
                    "item": "https://sudhan.live/resume"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": item.title,
                    "item": `https://sudhan.live/award/${idSlug}`
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        {/* Back */}
        <button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-accent font-medium mb-12 transition-smooth group">
          <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
          Back to Full Resume
        </button>

        <article className="bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

          {/* Header */}
          <header className="mb-8 relative z-10">
            {/* Type badge */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="bg-primary-900/50 text-primary-300 border border-primary-700/50 px-4 py-1.5 rounded-full text-sm font-semibold uppercase tracking-wide">
                {item.type === 'speaking' ? 'Outreach Activity' : item.type === 'outreach' ? 'Outreach Activity' : 'Award & Honor'}
              </span>
              {item.slug && item.slug.split('___')[0] && (
                <span className="bg-white/5 border border-white/10 px-4 py-1.5 rounded-full text-sm text-text-secondary font-medium">
                  {item.slug.split('___')[0]}
                </span>
              )}
            </div>

            {/* Logo + Title row */}
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {item.badge && (
                <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-2xl flex items-center justify-center p-3 overflow-hidden shadow-lg border border-white/20 flex-shrink-0">
                  <Image src={item.badge} alt={item.organization} className="w-full h-full object-contain" />
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-white/90 to-white/70 mb-3 leading-tight">
                  {item.title}
                </h1>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-text-secondary">
                  {item.organization && (
                    <span className="flex items-center gap-2 text-accent-light text-lg font-semibold">
                      <Icon name="Building2" size={18} /> {item.organization}
                    </span>
                  )}
                  {item.location && (
                    <span className="flex items-center gap-2 text-sm"><Icon name="MapPin" size={15} />{item.location}</span>
                  )}
                  {item.date_value && (
                    <span className="flex items-center gap-2 text-sm"><Icon name="Calendar" size={15} />{item.date_value}</span>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Image Slideshow */}
          {images.length > 0 && (
            <div className="mb-10 relative z-10">
              {/* Main slide */}
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black/20 cursor-pointer"
                onClick={() => openLightbox(slideIndex)}>
                <Image src={images[slideIndex]} alt={`Slide ${slideIndex + 1}`}
                  className="w-full h-full object-cover transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2.5 py-1.5 rounded-full text-xs text-white">
                  <Icon name="Expand" size={12} /> Click to expand
                </div>
                {images.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button key={i} onClick={(e) => { e.stopPropagation(); setSlideIndex(i); }}
                        className={`w-2 h-2 rounded-full transition-all ${i === slideIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'}`} />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {images.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setSlideIndex(i)}
                      className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === slideIndex ? 'border-accent scale-105' : 'border-white/10 hover:border-white/30'}`}>
                      <Image src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {item.description && (
            <section className="mb-8 relative z-10">
              <p className="text-text-secondary leading-relaxed text-base">{item.description}</p>
            </section>
          )}

          {/* Detailed Content */}
          {item.detailedContent && (
            <section className="mb-8 relative z-10 border-t border-white/10 pt-8">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Details</h2>
              <div className="text-text-secondary leading-relaxed prose prose-invert max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.detailedContent) }} />
            </section>
          )}

          {/* Key Highlights */}
          {bulletPoints.length > 0 && (
            <section className="mb-8 relative z-10 border-t border-white/10 pt-8">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Key Highlights</h2>
              <ul className="space-y-2">
                {bulletPoints.map((pt, i) => (
                  <li key={i} className="flex items-start gap-3 text-text-secondary text-sm">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0 shadow-[0_0_6px_rgba(14,165,233,0.4)]" />
                    {pt}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Technologies / Tags */}
          {technologies.length > 0 && (
            <section className="relative z-10 border-t border-white/10 pt-8">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {technologies.map(t => (
                  <span key={t} className="bg-accent/5 border border-accent/15 text-accent/80 px-3 py-1.5 rounded-full text-sm">{t}</span>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-5 right-5 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-smooth z-10">
            <Icon name="X" size={20} />
          </button>
          {lightboxIdx > 0 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => i - 1); }}
              className="absolute left-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-smooth z-10">
              <Icon name="ChevronLeft" size={24} />
            </button>
          )}
          <img src={images[lightboxIdx]} alt=""
            className="max-w-[90vw] max-h-[88vh] object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()} />
          {lightboxIdx < images.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => i + 1); }}
              className="absolute right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-smooth z-10">
              <Icon name="ChevronRight" size={24} />
            </button>
          )}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <button key={i} onClick={e => { e.stopPropagation(); setLightboxIdx(i); }}
                className={`w-2 h-2 rounded-full transition-all ${i === lightboxIdx ? 'bg-white scale-125' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AwardDetail;
